/**
 * 点击水波纹指令
 * 用法: v-ripple 或 v-ripple="true"
 * 在可点击元素上显示 Material 风格的水波纹反馈
 */

import type { Directive, DirectiveBinding } from 'vue'

const RIPPLE_CLASS = 'v-ripple-wave'
const RIPPLE_STYLES_ID = 'v-ripple-styles'

const ensureRippleStyles = (): void => {
  if (document.getElementById(RIPPLE_STYLES_ID)) return
  const style = document.createElement('style')
  style.id = RIPPLE_STYLES_ID
  style.textContent = `
    .v-ripple-wave {
      position: absolute;
      border-radius: 50%;
      background: var(--white-20, rgba(255, 255, 255, 0.2));
      pointer-events: none;
      transform: translate(-50%, -50%) scale(0);
      opacity: 1;
      transition: transform 0.5s ease-out, opacity 0.5s ease-out;
    }
    .v-ripple-wave.v-ripple-wave--active {
      transform: translate(-50%, -50%) scale(1);
      opacity: 0;
    }
  `
  document.head.appendChild(style)
}

const getRippleSize = (el: HTMLElement, x: number, y: number): number => {
  const rect = el.getBoundingClientRect()
  const centerX = rect.left + rect.width / 2
  const centerY = rect.top + rect.height / 2
  const distX = Math.max(Math.abs(x - centerX), rect.width / 2)
  const distY = Math.max(Math.abs(y - centerY), rect.height / 2)
  return Math.sqrt(distX * distX + distY * distY) * 2
}

const createRipple = (el: HTMLElement, clientX: number, clientY: number): void => {
  const rect = el.getBoundingClientRect()
  const x = clientX - rect.left
  const y = clientY - rect.top
  const size = getRippleSize(el, clientX, clientY)

  const ripple = document.createElement('span')
  ripple.className = RIPPLE_CLASS
  ripple.style.width = `${size}px`
  ripple.style.height = `${size}px`
  ripple.style.left = `${x}px`
  ripple.style.top = `${y}px`

  const handleTransitionEnd = (): void => {
    ripple.removeEventListener('transitionend', handleTransitionEnd)
    ripple.remove()
  }

  ripple.addEventListener('transitionend', handleTransitionEnd)
  el.appendChild(ripple)

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      ripple.classList.add(`${RIPPLE_CLASS}--active`)
    })
  })
}

const ensureRippleContainer = (el: HTMLElement): void => {
  const position = getComputedStyle(el).position
  if (position === 'static') {
    el.style.position = 'relative'
  }
  if (getComputedStyle(el).overflow !== 'hidden') {
    el.style.overflow = 'hidden'
  }
}

const RIPPLE_HANDLER_KEY = '__v_ripple_handler__'

type RippleHandlers = {
  mousedown: (e: MouseEvent) => void
  touchstart: (e: TouchEvent) => void
}

const handleRipple = (el: HTMLElement, e: MouseEvent | TouchEvent): void => {
  if (e.currentTarget !== el) return
  const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
  const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY
  createRipple(el, clientX, clientY)
}

export const vRipple: Directive = {
  mounted(el: HTMLElement, binding: DirectiveBinding<boolean | undefined>) {
    if (binding.value === false) return
    ensureRippleStyles()
    ensureRippleContainer(el)
    const handlers: RippleHandlers = {
      mousedown: (e: MouseEvent) => handleRipple(el, e),
      touchstart: (e: TouchEvent) => handleRipple(el, e),
    }
    ;(el as HTMLElement & { [RIPPLE_HANDLER_KEY]?: RippleHandlers })[RIPPLE_HANDLER_KEY] = handlers
    el.addEventListener('mousedown', handlers.mousedown)
    el.addEventListener('touchstart', handlers.touchstart, { passive: true })
  },

  updated(el: HTMLElement, binding: DirectiveBinding<boolean | undefined>) {
    if (binding.value === false) {
      el.style.position = ''
      el.style.overflow = ''
    } else {
      ensureRippleContainer(el)
    }
  },

  unmounted(el: HTMLElement) {
    const handlers = (el as HTMLElement & { [RIPPLE_HANDLER_KEY]?: RippleHandlers })[RIPPLE_HANDLER_KEY]
    if (handlers) {
      el.removeEventListener('mousedown', handlers.mousedown)
      el.removeEventListener('touchstart', handlers.touchstart)
      delete (el as HTMLElement & { [RIPPLE_HANDLER_KEY]?: RippleHandlers })[RIPPLE_HANDLER_KEY]
    }
    el.style.position = ''
    el.style.overflow = ''
  },
}
